import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import './App.css';
import LandingPage from './pages/LandingPage';
import SheetMapper from './pages/SheetMapper';
import Geocoding from './pages/Geocoding';
import QuickbaseMapper from './pages/QuickbaseMapper';
import ShareMap from './pages/ShareMap';
import CoordinatorMap from './pages/CoordinatorMap';
import CoordinatorMapSettings from './pages/CoordinatorMapSettings';
import FeedbackModal, { loadFeedbackDraft, clearFeedbackDraft } from './components/FeedbackModal';
import { checkAuthStatus, getAuthUrl } from './services/api';
import sheetMapIcon from './icons/SheetMapIcon.png';
import qbMapIcon from './icons/QBMapIcon.png';
import geocodingIcon from './icons/GeocodingIcon.png';

const APP_LABELS = {
  '/': 'Home',
  '/sheet-mapper': 'Google Sheet Mapper',
  '/geocoding': 'Bulk Geocoding',
  '/quickbase-mapper': 'QuickBase Mapper',
  '/coordinator-map': 'Coordinator Map',
  '/coordinator-map/settings': 'Coordinator Map Settings',
};

function appLabelForPath(pathname) {
  if (APP_LABELS[pathname]) return APP_LABELS[pathname];
  if (pathname.startsWith('/share/')) return 'Shared Map';
  if (pathname.startsWith('/coordinator-map')) return 'Coordinator Map';
  return 'SSN GEMS';
}

// Protected Route wrapper that requires authentication (and optionally Sheets)
function ProtectedRoute({
  isAuthenticated,
  authChecked,
  handleAuthenticate,
  requiresSheets = false,
  hasSheetsScope = false,
  children,
}) {
  if (!authChecked) return null; // Don't render anything while checking auth
  if (!isAuthenticated) {
    return (
      <AuthRequired
        handleAuthenticate={() => handleAuthenticate(
          requiresSheets ? { scopes: ['sheets'] } : undefined
        )}
        message={requiresSheets
          ? 'Sign in with your SSN Google account. This tool will also ask for Google Sheets access.'
          : 'You need to sign in with your SSN Google account to access this tool.'}
      />
    );
  }
  if (requiresSheets && !hasSheetsScope) {
    return (
      <AuthRequired
        title="Google Sheets Access Needed"
        message="This tool reads and writes Google Sheets. Allow access once (same Google screen as sign-in), then you’ll land back here."
        buttonLabel="Allow Google Sheets"
        handleAuthenticate={() => handleAuthenticate({ scopes: ['sheets'] })}
      />
    );
  }
  return children;
}

// Auth / scope gate message component
function AuthRequired({
  handleAuthenticate,
  title = 'Authentication Required',
  message = 'You need to sign in with your SSN Google account to access this tool.',
  buttonLabel = 'Sign in with Google',
}) {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '80vh',
      textAlign: 'center',
      padding: '2rem'
    }}>
      <h1 style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔒</h1>
      <h2>{title}</h2>
      <p style={{ color: '#666', maxWidth: '500px', marginBottom: '1rem' }}>
        {message}
      </p>
      {handleAuthenticate && (
        <button onClick={handleAuthenticate} className="auth-button" style={{
          marginBottom: '1rem',
          padding: '12px 24px',
          fontSize: '1.1rem',
          cursor: 'pointer'
        }}>
          {buttonLabel}
        </button>
      )}
      <Link to="/" style={{
        marginTop: '0.5rem',
        color: '#2B7A9B',
        textDecoration: 'none',
        fontSize: '0.95rem'
      }}>
        ← Back to Home
      </Link>
    </div>
  );
}

function Navigation({ isAuthenticated, handleAuthenticate, onOpenFeedback }) {
  const location = useLocation();
  const isLanding = location.pathname === '/';

  const authBlock = (
    <div className="auth-status">
      {isAuthenticated && (
        <button
          type="button"
          className="feedback-nav-button"
          onClick={onOpenFeedback}
          title="Report an issue or make a suggestion"
        >
          Feedback
        </button>
      )}
      {isAuthenticated ? (
        <span className="auth-badge authenticated">✓ Authenticated</span>
      ) : (
        <button onClick={handleAuthenticate} className="auth-button">
          Sign in with Google
        </button>
      )}
    </div>
  );

  if (isLanding) {
    return (
      <div className="top-nav-landing">
        <div className="nav-content">
          <div className="nav-left">
            <img src="/assets/ssn-logo.png" alt="SSN Logo" className="landing-logo" />
            <h1 className="nav-logo">SSN GEMS</h1>
          </div>
          {authBlock}
        </div>
      </div>
    );
  }

  const tabs = [
    { path: '/sheet-mapper', label: 'Google Sheet', icon: 'sheet-icon', requiresAuth: true },
    { path: '/geocoding', label: 'Geocoding', icon: 'geocoding-icon', requiresAuth: true },
    { path: '/quickbase-mapper', label: 'QuickBase', icon: 'qb-icon', requiresAuth: true },
    { path: '/coordinator-map', label: 'Coordinator Map', icon: '🗺️', requiresAuth: true }
  ];

  return (
    <div className="top-nav">
      <Link to="/" className="nav-brand">
        <img src="/assets/ssn-logo.png" alt="Site Services Now" className="nav-ssn-logo" />
        <div className="nav-brand-text">
          <h1 className="nav-logo">SSN GEMS</h1>
          <p className="nav-tagline">Geo-mapping Tools</p>
        </div>
      </Link>
      <div className="nav-tabs">
        {tabs.map(tab => (
          <Link
            key={tab.path}
            to={tab.disabled ? '#' : tab.path}
            className={`nav-tab ${location.pathname === tab.path ? 'active' : ''} ${tab.disabled ? 'disabled' : ''}`}
          >
            <span className="tab-icon">
              {tab.icon === 'sheet-icon' ? (
                <img src={sheetMapIcon} alt="" className="nav-icon-img" />
              ) : tab.icon === 'geocoding-icon' ? (
                <img src={geocodingIcon} alt="" className="nav-icon-img" />
              ) : tab.icon === 'qb-icon' ? (
                <img src={qbMapIcon} alt="" className="nav-icon-img" />
              ) : (
                tab.icon
              )}
            </span>
            <span className="tab-label">{tab.label}</span>
            {tab.disabled && <span className="tab-badge">Soon</span>}
          </Link>
        ))}
      </div>
      {authBlock}
    </div>
  );
}

function AppShell({
  isAuthenticated,
  authChecked,
  handleAuthenticate,
  handleGmailAuth,
  hasSheetsScope,
  user,
  feedbackOpen,
  setFeedbackOpen,
  feedbackDraft,
  setFeedbackDraft,
}) {
  const location = useLocation();

  useEffect(() => {
    const onSuggest = (e) => {
      setFeedbackDraft({
        category: 'suggestion',
        message: e.detail?.message || 'Filter preset idea: ',
      });
      setFeedbackOpen(true);
    };
    window.addEventListener('gems:open-feedback', onSuggest);
    return () => window.removeEventListener('gems:open-feedback', onSuggest);
  }, [setFeedbackDraft, setFeedbackOpen]);

  const closeFeedback = () => {
    setFeedbackOpen(false);
    setFeedbackDraft(null);
    clearFeedbackDraft();
  };

  const sheetsRouteProps = {
    isAuthenticated,
    authChecked,
    handleAuthenticate,
    requiresSheets: true,
    hasSheetsScope,
  };

  const authRouteProps = {
    isAuthenticated,
    authChecked,
    handleAuthenticate,
  };

  return (
    <>
      <Navigation
        isAuthenticated={isAuthenticated}
        handleAuthenticate={() => handleAuthenticate()}
        onOpenFeedback={() => {
          setFeedbackDraft(null);
          setFeedbackOpen(true);
        }}
      />
      <Routes>
        <Route path="/" element={
          <LandingPage
            isAuthenticated={isAuthenticated}
            handleAuthenticate={handleAuthenticate}
            hasSheetsScope={hasSheetsScope}
          />
        } />
        <Route path="/sheet-mapper" element={
          <ProtectedRoute {...sheetsRouteProps}>
            <SheetMapper />
          </ProtectedRoute>
        } />
        <Route path="/geocoding" element={
          <ProtectedRoute {...sheetsRouteProps}>
            <Geocoding />
          </ProtectedRoute>
        } />
        <Route path="/quickbase-mapper" element={
          <ProtectedRoute {...authRouteProps}>
            <QuickbaseMapper />
          </ProtectedRoute>
        } />
        <Route path="/share/:shareId" element={
          <ProtectedRoute {...authRouteProps}>
            <ShareMap />
          </ProtectedRoute>
        } />
        <Route path="/coordinator-map" element={
          <ProtectedRoute {...authRouteProps}>
            <CoordinatorMap />
          </ProtectedRoute>
        } />
        <Route path="/coordinator-map/settings" element={
          <ProtectedRoute {...authRouteProps}>
            <CoordinatorMapSettings />
          </ProtectedRoute>
        } />
      </Routes>
      <FeedbackModal
        open={feedbackOpen}
        onClose={closeFeedback}
        appPath={location.pathname}
        appLabel={appLabelForPath(location.pathname)}
        userEmail={user?.email}
        userName={user?.name}
        onNeedsGmailAuth={handleGmailAuth}
        initialDraft={feedbackDraft}
      />
    </>
  );
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [user, setUser] = useState(null);
  const [scopes, setScopes] = useState({ sheets: false, gmail: false });
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackDraft, setFeedbackDraft] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      const data = await checkAuthStatus();
      setIsAuthenticated(!!data.authenticated);
      setScopes({
        sheets: !!data.scopes?.sheets,
        gmail: !!data.scopes?.gmail,
      });
      setUser(data.authenticated
        ? {
            email: data.email || '',
            name: data.name || '',
            givenName: data.givenName || '',
            familyName: data.familyName || '',
          }
        : null);
    } catch (err) {
      console.error('Auth check failed:', err);
      setIsAuthenticated(false);
      setUser(null);
      setScopes({ sheets: false, gmail: false });
    } finally {
      setAuthChecked(true);
    }
  }, []);

  useEffect(() => {
    checkAuth();

    // Re-check auth after OAuth redirect
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('auth') === 'success') {
      const returnPath = sessionStorage.getItem('authReturnPath');
      sessionStorage.removeItem('authReturnPath');

      // Redirect to the original page if it wasn't the landing page
      if (returnPath && returnPath !== '/') {
        window.location.href = returnPath;
        return;
      }

      // Remove auth parameter from URL and re-check authentication
      window.history.replaceState({}, '', window.location.pathname);
      setTimeout(() => checkAuth(), 500);
    }

    // After returning from optional Gmail consent, reopen the feedback draft
    const draft = loadFeedbackDraft();
    if (draft?.message) {
      setFeedbackDraft(draft);
      setFeedbackOpen(true);
    }
  }, [checkAuth]);

  /**
   * @param {{ scopes?: string[], returnPath?: string }} [options]
   * - No options → identity login only
   * - scopes: ['sheets'] / ['gmail'] → request those (incremental if already signed in)
   * - returnPath → where to land after Google redirects back
   */
  const handleAuthenticate = async (options = {}) => {
    try {
      const returnPath = options.returnPath || window.location.pathname;
      sessionStorage.setItem('authReturnPath', returnPath);
      const { authUrl } = await getAuthUrl(
        options.scopes?.length ? { scopes: options.scopes } : {}
      );
      window.location.href = authUrl;
    } catch (err) {
      console.error('Failed to initiate authentication');
    }
  };

  // Incremental Gmail consent for feedback only
  const handleGmailAuth = () => handleAuthenticate({ scopes: ['gmail'] });

  return (
    <Router>
      <div className="app-container">
        <AppShell
          isAuthenticated={isAuthenticated}
          authChecked={authChecked}
          handleAuthenticate={handleAuthenticate}
          handleGmailAuth={handleGmailAuth}
          hasSheetsScope={scopes.sheets}
          user={user}
          feedbackOpen={feedbackOpen}
          setFeedbackOpen={setFeedbackOpen}
          feedbackDraft={feedbackDraft}
          setFeedbackDraft={setFeedbackDraft}
        />
      </div>
    </Router>
  );
}

function ComingSoon({ tool }) {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '80vh',
      textAlign: 'center',
      padding: '2rem'
    }}>
      <h1 style={{ fontSize: '3rem', marginBottom: '1rem' }}>🚧</h1>
      <h2>{tool}</h2>
      <p style={{ color: '#666', maxWidth: '500px' }}>
        This tool is currently under development. Check back soon!
      </p>
      <Link to="/" style={{ marginTop: '2rem', color: '#667eea', textDecoration: 'none', fontSize: '1.1rem' }}>
        ← Back to Home
      </Link>
    </div>
  );
}

export default App;
